import { useState, useEffect, useMemo } from 'react';
import { Tag, Edit2, Calendar, Type, Hash } from 'lucide-react';
import type { CustomField, CustomFieldValue, Task } from '@/types';
import { fetchCustomFieldsForContext } from '@/db/customFields';
import { getProjectIdByPath as getSpaceIdByPath } from '@/db/projects';
import { getCustomFieldsCacheKey } from '@/utils/customFieldsCache';
import { CustomFieldEditor } from './CustomFieldEditor';
import { handleError } from '@/utils/errorHandler';
import clsx from 'clsx';

// Extended task type to include path property
type TaskWithPath = Task & { path?: string };

interface CustomFieldsInlineProps {
  task: TaskWithPath;
  onUpdate?: (updates: Partial<Task>) => void;
  maxDisplay?: number;
  size?: 'sm' | 'md';
}

// Simple cache for custom fields to avoid redundant loads
const customFieldsCache = new Map<string, { fields: CustomField[]; timestamp: number }>();
const CACHE_TTL = 60000; // 1 minute

export function CustomFieldsInline({
  task,
  onUpdate,
  maxDisplay = 3,
  size = 'sm',
}: CustomFieldsInlineProps) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  useEffect(() => {
    const loadFields = async () => {
      setLoading(true);
      try {
        // Early return if no location info available (task not yet enriched)
        if (!task.spaceName && !task.path) {
          setFields([]);
          setLoading(false);
          return;
        }

        const cacheKey = getCustomFieldsCacheKey(task.spaceName, task.folderPath, task.listName);
        const cached = customFieldsCache.get(cacheKey);

        // Check cache validity
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
          setFields(cached.fields);
          setLoading(false);
          return;
        }

        // Resolve containerId: use spaceId directly if available (most reliable)
        let containerId: string | undefined = task.spaceId || undefined;

        // Fallback: resolve from spaceName
        if (!containerId && task.spaceName) {
          const pathParts: string[] = [task.spaceName];
          if (task.folderPath && task.folderPath.length > 0) {
            pathParts.push(task.folderPath[0]);
          }
          if (task.listName) {
            pathParts.push(task.listName);
          }
          const path = pathParts.join('/');
          containerId = (await getSpaceIdByPath(path)) || undefined;
        }

        // If we couldn't resolve a containerId, return empty fields
        if (!containerId) {
          setFields([]);
          setLoading(false);
          return;
        }

        // For custom fields, use containerId for all scopes
        const fetchedFields = await fetchCustomFieldsForContext(containerId, undefined, containerId);
        setFields(fetchedFields);

        // Update cache
        customFieldsCache.set(cacheKey, { fields: fetchedFields, timestamp: Date.now() });
      } catch (err) {
        handleError(err, { level: 'warning', message: 'Errore nel caricamento dei campi personalizzati' });
      } finally {
        setLoading(false);
      }
    };

    loadFields();
  }, [task.spaceId, task.spaceName, task.folderPath, task.listName, task.path]);

  const handleCustomFieldChange = (fieldId: string, value: CustomFieldValue) => {
    if (onUpdate) {
      onUpdate({
        customFields: {
          ...task.customFields,
          [fieldId]: value ?? null,
        },
      });
      setEditingFieldId(null);
    }
  };

  // Show all fields, prioritizing those with values
  const visibleFields = useMemo(() => {
    if (loading) return [];

    const fieldsWithValues: CustomField[] = [];
    const fieldsWithoutValues: CustomField[] = [];

    fields.forEach((field) => {
      const value = task.customFields?.[field.id];
      // Check if value is a valid CustomFieldValue (not an empty object)
      const isValidValue =
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (typeof value !== 'object' || Array.isArray(value) || value instanceof Date);
      const hasValue =
        isValidValue &&
        ((Array.isArray(value) && value.length > 0) ||
          (!Array.isArray(value) && value !== null && value !== undefined && value !== ''));

      if (hasValue) {
        fieldsWithValues.push(field);
      } else if (onUpdate) {
        // Only show empty fields if editing is enabled
        fieldsWithoutValues.push(field);
      }
    });

    // Prioritize fields with values, then add empty editable fields up to maxDisplay
    const result = [...fieldsWithValues];
    const remaining = maxDisplay - result.length;
    if (remaining > 0) {
      result.push(...fieldsWithoutValues.slice(0, remaining));
    }

    return result.slice(0, maxDisplay);
  }, [fields, task.customFields, loading, maxDisplay, onUpdate]);

  if (loading || visibleFields.length === 0) {
    return null;
  }

  const canEdit = !!onUpdate;
  const textSize = size === 'sm' ? 'text-[10px]' : 'text-[11px]';
  const paddingSize = size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-1';

  const formatValue = (field: CustomField, value: CustomFieldValue): string => {
    if (value === null || value === undefined || value === '') return '';

    if (field.type === 'date') {
      // Handle both Date objects and timestamps (numbers)
      const date =
        value instanceof Date ? value : typeof value === 'number' ? new Date(value) : null;
      if (!date || isNaN(date.getTime())) return '';
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
    }

    if (field.type === 'text') {
      return String(value);
    }

    if (field.type === 'number') {
      const num = typeof value === 'number' ? value : Number(value);
      if (isNaN(num)) return '';
      return num.toLocaleString('it-IT');
    }

    return '';
  };

  return (
    <div
      className="flex items-center gap-1 shrink-0 flex-wrap"
      onClick={(e) => e.stopPropagation()}
    >
      {visibleFields.map((field) => {
        const value = task.customFields?.[field.id];
        const hasValue = value !== null && value !== undefined && value !== '';

        // For dropdown with values, show badge
        if (field.type === 'dropdown' && hasValue) {
          const option = field.options?.find((opt) => opt.value === value);
          if (!option) return null;

          return (
            <span
              key={field.id}
              className={clsx(
                'inline-flex items-center gap-1 rounded font-medium bg-accent-violet/10 text-accent-violet border border-accent-violet/20',
                textSize,
                paddingSize,
              )}
              title={field.name}
            >
              {option.color && (
                <div
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: option.color }}
                />
              )}
              <span className="truncate max-w-[60px]">{option.label}</span>
            </span>
          );
        }

        // For text/number/date with values, show compact display
        if (
          hasValue &&
          (field.type === 'text' || field.type === 'date' || field.type === 'number')
        ) {
          // Ensure value is a valid CustomFieldValue (not an object)
          const fieldValue: CustomFieldValue = Array.isArray(value)
            ? value
            : typeof value === 'object' && value instanceof Date
              ? value
              : typeof value === 'object' && value !== null
                ? null
                : (value as CustomFieldValue);
          const displayValue = formatValue(field, fieldValue);
          if (!displayValue) return null;

          return (
            <div
              key={field.id}
              className={clsx(
                'inline-flex items-center gap-1 rounded font-medium bg-surface-tertiary text-text-secondary border border-border-primary',
                textSize,
                paddingSize,
                canEdit && 'cursor-pointer hover:bg-surface-hover'
              )}
              title={field.name}
              onClick={(e) => {
                if (canEdit) {
                  e.stopPropagation();
                  setEditingFieldId(field.id);
                }
              }}
            >
              {field.type === 'date' && <Calendar className="w-2.5 h-2.5 shrink-0" />}
              {field.type === 'text' && <Type className="w-2.5 h-2.5 shrink-0" />}
              {field.type === 'number' && <Hash className="w-2.5 h-2.5 shrink-0" />}
              <span className="truncate max-w-[60px]">{displayValue}</span>
              {canEdit && <Edit2 className="w-2.5 h-2.5 shrink-0 opacity-50" />}
            </div>
          );
        }

        // For empty fields, show editable placeholder if editing is enabled
        if (!hasValue && canEdit) {
          if (editingFieldId === field.id) {
            // Convert timestamp to Date for date fields
            let editorValue: CustomFieldValue = value ?? null;
            if (field.type === 'date' && value && typeof value === 'number') {
              editorValue = new Date(value);
            }

            return (
              <div
                key={field.id}
                className="shrink-0"
                style={{ minWidth: size === 'sm' ? '80px' : '100px', maxWidth: '120px' }}
              >
                <CustomFieldEditor
                  field={field}
                  value={editorValue}
                  onChange={(newValue) => handleCustomFieldChange(field.id, newValue)}
                  disabled={false}
                />
              </div>
            );
          }

          return (
            <button
              key={field.id}
              onClick={(e) => {
                e.stopPropagation();
                setEditingFieldId(field.id);
              }}
              className={clsx(
                'inline-flex items-center gap-1 rounded font-medium bg-surface-tertiary/50 text-text-quaternary border border-border-primary/50 hover:bg-surface-hover hover:border-border-primary transition-colors',
                textSize,
                paddingSize
              )}
              title={`Aggiungi ${field.name}`}
            >
              {field.type === 'date' && <Calendar className="w-2.5 h-2.5 shrink-0" />}
              {field.type === 'text' && <Type className="w-2.5 h-2.5 shrink-0" />}
              {field.type === 'number' && <Hash className="w-2.5 h-2.5 shrink-0" />}
              {field.type === 'dropdown' && <Tag className="w-2.5 h-2.5 shrink-0" />}
              <span className="truncate max-w-[50px]">{field.name}</span>
              <Edit2 className="w-2.5 h-2.5 shrink-0 opacity-50" />
            </button>
          );
        }

        return null;
      })}
    </div>
  );
}
